import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import PageNavigation from "@/components/PageNavigation";
import SEOHead from "@/components/seo/SEOHead";
import { Button } from "@/components/ui/button";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Clock, User, BookOpen, Target } from "lucide-react";
import coverProspeccao from "@/assets/cover-prospeccao-b2b.jpg";

const ProspeccaoB2B = () => {
  const navigate = useNavigate();

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline:
      "Prospecção B2B: Como Prospectar Clientes B2B e Encher o Pipeline Sem Depender de Indicação",
    description:
      "Guia prático de prospecção B2B: como prospectar clientes B2B, montar cadência de prospecção outbound, escolher canais e gerar leads B2B qualificados de forma previsível.",
    image: "https://bianchimbusiness.com.br/cover-prospeccao-b2b.jpg",
    datePublished: "2026-09-02",
    author: { "@type": "Person", name: "Junior Bianchim" },
    publisher: {
      "@type": "Organization",
      name: "Bianchim Business",
      logo: { "@type": "ImageObject", url: "https://bianchimbusiness.com.br/logo.png" },
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": "https://bianchimbusiness.com.br/posts/prospeccao-b2b",
    },
  };

  const canais = [
    {
      canal: "Ligação (cold call)",
      quando: "Ticket médio e alto, decisor acessível por telefone",
      taxa: "Contato efetivo: 12% a 20% das discagens | Reunião: 2% a 5%",
      obs: "Ainda é o canal que mais gera reunião por hora trabalhada quando o script é bom",
    },
    {
      canal: "E-mail frio",
      quando: "Listas grandes, decisores em TI, finanças e indústria",
      taxa: "Abertura: 30% a 45% | Resposta: 3% a 8%",
      obs: "Assunto curto, 90 palavras no corpo, um único pedido no fim",
    },
    {
      canal: "LinkedIn / social selling",
      quando: "C-level, serviços, consultoria e vendas complexas",
      taxa: "Aceite de convite: 25% a 40% | Resposta: 8% a 15%",
      obs: "Aquecer com conteúdo antes de pedir reunião dobra a resposta",
    },
    {
      canal: "WhatsApp",
      quando: "PMEs, comércio, indústria regional",
      taxa: "Resposta: 20% a 35%",
      obs: "Só depois de um contato anterior ou opt-in — frio demais queima a marca",
    },
    {
      canal: "Indicação ativa (referral)",
      quando: "Carteira existente e clientes satisfeitos",
      taxa: "Conversão em reunião: 40% a 60%",
      obs: "O canal mais barato e o mais esquecido: peça indicação em todo pós-venda",
    },
    {
      canal: "Eventos e associações setoriais",
      quando: "Ticket alto, ciclo longo, mercados fechados",
      taxa: "Baixo volume, alta qualidade",
      obs: "Vale pelo acesso ao decisor, não pelo número de cartões",
    },
  ];

  const cadencia = [
    { dia: "Dia 1", acao: "Ligação + e-mail curto de contexto", canal: "Telefone / e-mail" },
    { dia: "Dia 2", acao: "Convite no LinkedIn sem pitch", canal: "LinkedIn" },
    { dia: "Dia 4", acao: "Ligação em horário diferente do dia 1", canal: "Telefone" },
    { dia: "Dia 6", acao: "E-mail com prova social do mesmo setor", canal: "E-mail" },
    { dia: "Dia 9", acao: "Mensagem no LinkedIn com insight, não com pedido", canal: "LinkedIn" },
    { dia: "Dia 12", acao: "Ligação para o gatekeeper mapeando outro decisor", canal: "Telefone" },
    { dia: "Dia 15", acao: "E-mail de encerramento (break-up) educado", canal: "E-mail" },
  ];

  const erros = [
    {
      erro: "Prospectar sem ICP definido",
      efeito: "Volume alto, conversão baixa e vendedor desmotivado",
      correcao: "Feche a lista por porte, setor, região e dor recorrente antes de discar",
    },
    {
      erro: "Falar de produto no primeiro contato",
      efeito: "O decisor entende como propaganda e desliga",
      correcao: "Abra com um problema típico do setor dele e peça permissão de 30 segundos",
    },
    {
      erro: "Parar no segundo toque",
      efeito: "Perde a maior parte das reuniões possíveis",
      correcao: "Cadência de 7 a 9 toques em 15 dias, com canais alternados",
    },
    {
      erro: "Misturar prospecção com atendimento",
      efeito: "A agenda de prospecção é a primeira a ser sacrificada",
      correcao: "Bloco fixo diário de prospecção, sem CRM aberto em outra aba",
    },
    {
      erro: "Não medir por etapa",
      efeito: "Ninguém sabe se o gargalo é lista, abordagem ou agendamento",
      correcao: "Meça toques, contatos efetivos, reuniões marcadas e reuniões realizadas",
    },
  ];

  return (
    <>
      <SEOHead
        title="Prospecção B2B: Como Prospectar Clientes B2B em 2026 | Bianchim Business"
        description="Como prospectar clientes B2B na prática: ICP, lista, cadência de prospecção outbound, canais que mais geram reunião e métricas de geração de leads B2B."
        keywords="prospecção B2B, como prospectar clientes B2B, prospecção outbound, geração de leads B2B, cadência de prospecção, cold call B2B, lista de prospecção, SDR, pipeline B2B"
        canonicalUrl="https://bianchimbusiness.com.br/posts/prospeccao-b2b"
        ogType="article"
        articlePublishedTime="2026-09-02"
        articleAuthor="Junior Bianchim"
      />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />

      <div className="min-h-screen bg-background">
        <Header />

        <main>
          <article>
            {/* Hero */}
            <section className="relative">
              <div className="w-full h-[46vh] sm:h-[58vh] relative overflow-hidden">
                <img
                  src={coverProspeccao}
                  alt="Time comercial fazendo prospecção B2B com rede de contatos e pipeline de leads"
                  className="w-full h-full object-cover"
                  width={1600}
                  height={912}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-background via-background/60 to-transparent" />
              </div>
              <div className="container mx-auto px-4 relative -mt-28 sm:-mt-40 z-10">
                <div className="max-w-4xl mx-auto">
                  <span className="inline-block px-4 py-1.5 rounded-full bg-primary/20 text-primary text-sm font-semibold mb-4">
                    Prospecção
                  </span>
                  <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold leading-tight mb-4 text-foreground">
                    Prospecção B2B:{" "}
                    <span className="text-gradient-brand">
                      como prospectar clientes e encher o pipeline sem depender de indicação
                    </span>
                  </h1>
                  <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground mt-6">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4" /> <span>Junior Bianchim</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4" /> <span>13 min de leitura</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <BookOpen className="w-4 h-4" /> <span>02 Set 2026</span>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Conteúdo */}
            <section className="container mx-auto px-4 py-12">
              <div className="max-w-4xl mx-auto space-y-8 text-base sm:text-lg leading-relaxed text-muted-foreground">
                <p>
                  Toda empresa B2B que sofre com meta tem o mesmo sintoma: o mês começa com o
                  pipeline vazio e o time correndo atrás de quem já conhecia. Enquanto a receita
                  depender de indicação e de cliente que aparece sozinho, o crescimento vai ser
                  sempre uma aposta. Prospecção B2B é o que transforma faturamento em algo que se
                  planeja, não em algo que se torce.
                </p>
                <p>
                  Neste guia eu mostro como prospectar clientes B2B na prática: como definir o
                  cliente ideal, montar a lista, escolher os canais, construir uma cadência de
                  prospecção outbound que realmente marca reunião e quais números acompanhar para
                  saber se a geração de leads B2B está saudável.
                </p>

                <h2 className="text-2xl sm:text-3xl font-bold text-foreground pt-4">
                  1. Antes da lista: quem é o cliente que vale a sua hora
                </h2>
                <p>
                  Prospecção ruim quase nunca é problema de discurso — é problema de destinatário. Se
                  o vendedor liga para empresas que não têm a dor, não têm orçamento ou não têm
                  urgência, nenhum script salva. Antes de qualquer coisa, escreva o seu ICP (perfil
                  de cliente ideal) com quatro filtros objetivos: porte (faturamento ou número de
                  vendedores), setor, região e o gatilho que indica que a dor está viva agora.
                </p>
                <p>
                  Gatilhos são o que separa prospecção de spam. Empresa que acabou de contratar
                  vendedores, que abriu filial, que trocou o diretor comercial, que começou a
                  anunciar vagas de SDR ou que está investindo pesado em mídia tem motivo concreto
                  para ouvir você esta semana. Uma lista de 80 empresas com gatilho vale mais que
                  uma planilha de 3 mil CNPJs comprados.
                </p>
                <p>
                  Uma conta simples fecha esse raciocínio: se o seu ticket é de R$ 12 mil e você
                  precisa de R$ 300 mil no mês, são 25 contratos. Com win rate de 25% e conversão de
                  reunião em proposta de 50%, você precisa de 200 reuniões realizadas. Isso é o que
                  define quantos toques por dia o time precisa dar — e não o "vamos dar o máximo".
                </p>

                <h2 className="text-2xl sm:text-3xl font-bold text-foreground pt-4">
                  2. Canais de prospecção outbound: onde o decisor realmente responde
                </h2>
                <p>
                  Não existe canal melhor, existe canal certo para o seu decisor. O erro comum é
                  apostar tudo em um só. Prospecção outbound funciona quando o mesmo contato é
                  abordado por dois ou três canais em sequência, porque cada toque aumenta o
                  reconhecimento e reduz a resistência do próximo.
                </p>

                <div className="overflow-x-auto rounded-xl border border-border">
                  <table className="w-full text-sm text-left">
                    <thead className="bg-muted/50 text-foreground">
                      <tr>
                        <th className="p-3 font-semibold">Canal</th>
                        <th className="p-3 font-semibold">Quando usar</th>
                        <th className="p-3 font-semibold">Faixa de resposta</th>
                        <th className="p-3 font-semibold">O que faz diferença</th>
                      </tr>
                    </thead>
                    <tbody>
                      {canais.map((c) => (
                        <tr key={c.canal} className="border-t border-border align-top">
                          <td className="p-3 font-medium text-foreground">{c.canal}</td>
                          <td className="p-3">{c.quando}</td>
                          <td className="p-3">{c.taxa}</td>
                          <td className="p-3">{c.obs}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="text-sm">
                  As faixas acima são referências de operações B2B no Brasil e servem para você se
                  localizar. Compare sempre com o seu próprio histórico antes de trocar de canal.
                </p>

                <h2 className="text-2xl sm:text-3xl font-bold text-foreground pt-4">
                  3. A cadência que marca reunião: 7 a 9 toques em 15 dias
                </h2>
                <p>
                  A maior parte das reuniões B2B nasce entre o quinto e o oitavo contato. E a maior
                  parte dos times para no segundo. Cadência não é insistência: é presença
                  organizada, com canais alternados e uma mensagem diferente a cada toque.
                </p>

                <div className="overflow-x-auto rounded-xl border border-border">
                  <table className="w-full text-sm text-left">
                    <thead className="bg-muted/50 text-foreground">
                      <tr>
                        <th className="p-3 font-semibold">Momento</th>
                        <th className="p-3 font-semibold">Ação</th>
                        <th className="p-3 font-semibold">Canal</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cadencia.map((c) => (
                        <tr key={c.dia} className="border-t border-border align-top">
                          <td className="p-3 font-medium text-foreground">{c.dia}</td>
                          <td className="p-3">{c.acao}</td>
                          <td className="p-3">{c.canal}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p>
                  Repare que nenhum toque pede "uma apresentação da empresa". O pedido é sempre o
                  próximo passo mínimo: 15 minutos, uma conversa curta, uma opinião. Se você quiser
                  um modelo pronto para adaptar a abordagem de cada toque, use o{" "}
                  <Link to="/posts/script-de-vendas-pronto" className="text-primary underline">
                    script de vendas pronto
                  </Link>{" "}
                  e a estrutura de{" "}
                  <Link to="/posts/pre-vendas-sdr-bdr-2026" className="text-primary underline">
                    pré-vendas com SDR e BDR
                  </Link>
                  .
                </p>

                <div className="rounded-xl border border-primary/30 bg-gradient-to-br from-primary/10 to-accent/10 p-6 sm:p-8 mt-10">
                  <div className="flex items-center gap-2 mb-3 text-primary">
                    <Target className="w-5 h-5" />
                    <span className="font-semibold">Treine a abordagem antes de queimar a lista</span>
                  </div>
                  <h3 className="text-xl sm:text-2xl font-bold text-foreground mb-3">
                    Cada ligação mal feita é um cliente que você não terá de novo
                  </h3>
                  <p className="mb-5">
                    Lista boa com abordagem fraca é dinheiro jogado fora: o decisor só atende uma
                    vez. No Vendas Trainer, o vendedor simula a prospecção com personas de IA do
                    perfil que ele vai abordar — gatekeeper, gerente ocupado, diretor cético — e
                    recebe nota de 0 a 100 com feedback de abertura, descoberta, objeção e
                    agendamento. O erro acontece no treino, não no telefone do seu cliente.
                  </p>
                  <div className="flex flex-wrap gap-3">
                    <Button
                      onClick={() => navigate("/auth?mode=signup")}
                      className="bg-primary text-primary-foreground hover:bg-primary/90"
                    >
                      Criar conta grátis e treinar prospecção
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                    <Button variant="outline" onClick={() => navigate("/calculadora-roi")}>
                      Calcular o ROI do treinamento
                    </Button>
                  </div>
                </div>

                <h2 className="text-2xl sm:text-3xl font-bold text-foreground pt-4">
                  4. A abertura que faz o decisor continuar na linha
                </h2>
                <p>
                  Os primeiros 15 segundos decidem a ligação. O que funciona é ser direto sobre quem
                  você é, dar um motivo específico para estar ligando para aquela empresa e pedir
                  permissão. Algo como: "Marcius, aqui é o Junior da Bianchim Business. Vi que vocês
                  ampliaram o time comercial esse ano. Eu ligo justamente para empresas que
                  contrataram vendedores e ainda estão sentindo demora até eles produzirem. Posso te
                  roubar 30 segundos para saber se isso faz sentido aí?"
                </p>
                <p>
                  Depois da permissão, o jogo é pergunta, não discurso. Duas ou três perguntas de
                  diagnóstico — quanto tempo um vendedor novo leva para bater meta, o que hoje mais
                  atrasa isso — já dão material suficiente para propor a reunião com um motivo que é
                  dele, não seu. Para aprofundar o contorno das respostas negativas, vale ler o guia
                  sobre{" "}
                  <Link to="/posts/objecoes-preco" className="text-primary underline">
                    objeções de preço
                  </Link>
                  .
                </p>

                <h2 className="text-2xl sm:text-3xl font-bold text-foreground pt-4">
                  5. Os cinco erros que matam a geração de leads B2B
                </h2>

                <div className="overflow-x-auto rounded-xl border border-border">
                  <table className="w-full text-sm text-left">
                    <thead className="bg-muted/50 text-foreground">
                      <tr>
                        <th className="p-3 font-semibold">Erro</th>
                        <th className="p-3 font-semibold">O que acontece</th>
                        <th className="p-3 font-semibold">Correção</th>
                      </tr>
                    </thead>
                    <tbody>
                      {erros.map((e) => (
                        <tr key={e.erro} className="border-t border-border align-top">
                          <td className="p-3 font-medium text-foreground">{e.erro}</td>
                          <td className="p-3">{e.efeito}</td>
                          <td className="p-3">{e.correcao}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <h2 className="text-2xl sm:text-3xl font-bold text-foreground pt-4">
                  6. Como medir se a prospecção está saudável
                </h2>
                <p>
                  Prospecção se mede por etapa, nunca por "quantas ligações fez". Acompanhe
                  semanalmente: toques realizados, contatos efetivos (falou com o decisor), reuniões
                  marcadas, reuniões realizadas (o no-show acima de 30% é sinal de agendamento
                  fraco) e reuniões que viraram oportunidade real. Cada queda brusca entre duas
                  etapas aponta o problema exato: lista, abordagem, agendamento ou qualificação.
                </p>
                <p>
                  Uma régua prática por SDR em operação B2B: 50 a 70 toques por dia, 8 a 12 contatos
                  efetivos, 2 a 4 reuniões marcadas e 8 a 12 reuniões realizadas por semana. Se você
                  quiser calibrar esses números com os indicadores do restante do funil, use o guia
                  de{" "}
                  <Link to="/posts/metricas-e-kpis-de-vendas" className="text-primary underline">
                    métricas e KPIs de vendas
                  </Link>
                  .
                </p>

                <h2 className="text-2xl sm:text-3xl font-bold text-foreground pt-4">
                  Conclusão: pipeline cheio é rotina, não talento
                </h2>
                <p>
                  Prospecção B2B não depende de vendedor iluminado. Depende de lista certa, cadência
                  respeitada, abordagem treinada e leitura honesta dos números. Escolha 100 empresas
                  com gatilho, rode a cadência de 15 dias sem pular toque e meça cada etapa. Em dois
                  ciclos você já sabe exatamente onde está o gargalo — e aí sim faz sentido mudar
                  discurso, canal ou oferta. Para estruturar o time que vai executar isso, veja como{" "}
                  <Link to="/posts/como-montar-equipe-de-vendas-do-zero" className="text-primary underline">
                    montar uma equipe de vendas do zero
                  </Link>
                  .
                </p>
              </div>
            </section>

            <PageNavigation />
          </article>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default ProspeccaoB2B;
